<script setup lang="ts">

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useWindowSize} from "@vueuse/core";
import {useBlogStore} from "@/stores/blogStore";
import {useRoute, useRouter} from "vue-router";
import CustomButton from "@/components/Button/Button.vue";
import ArticleCard from "@/components/ArticleCard/ArticleCard.vue";
import {useArticleStore} from "@/stores/articleStore";
import {Page} from "@/models/Page";
import {VueSpinner} from "vue3-spinners";

const route = useRoute();
const router = useRouter();

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

const blogStore = useBlogStore();
const articleStore = useArticleStore();
const slug = "travel-inspiration";
const loading = ref(false);
const page = ref<Page>({});

const loadPage = () => {
  loading.value = true;
  articleStore.getPage(route.params.slug)
      .then((data) => {
        page.value = data.data;
      })
      .catch(() => {
        route.push({ path: 'notfound' });
      })
      .finally(() => {
        loading.value = false;
      });
};

const backgroundImageUrl = computed(() => {
  if (!page.value?.imageSet) return '';

  const imageSet = page.value.imageSet;
  return isMobile.value
      ? imageSet['default']
      : imageSet['1600w'] || Object.values(imageSet)[0];
});

watch(route, () => {
  loadPage()
});

onMounted(async  () => {
  loadPage()
  await blogStore.getAllArticles(slug, blogStore.currentPage, blogStore.articlesPerPage)
  articleStore.selectedArticleSlug = route.params.slug as string;
})

onUnmounted(() => {
  articleStore.selectedArticleSlug = '';
});

watch(
    () => route.params.slug,
    async (newSlug) => {
      if (typeof newSlug === 'string') {
        await articleStore.fetchArticle(newSlug)
      }
    }
)

</script>

<template>

  <div v-if="loading" class="grid place-content-center mt-10">
    <VueSpinner size="30" color="#beA5aa" />
  </div>

  <!-- Hero Section -->
  <section v-if="page.imageSet"
      class="relative w-full h-[80vh] sm:h-[75vh] md:h-[70vh] xl:h-[70vh] bg-cover bg-center bg-no-repeat"
           :style="`background-image: url('${backgroundImageUrl}')`"
  />
  <section v-else class="mt-32"></section>

  <!-- Article Content -->
  <div class="relative z-10 -mt-20 bg-background-beige px-4 py-12 sm:container w-full max-w-[1500px] mx-auto">
    <h1 class="responsive-title-1 font-bold pb-9 text-primary-darkBlue sm:px-12 ">
      {{ page.title }}
    </h1>

    <div class="responsive-body-large text-primary-darkBlue mix-blend-multiply sm:px-12">
      <div v-html="page.rendered" />
    </div>
  </div>

  <div class="bg-background-light mt-24">
    <div class="container flex flex-col gap-17 place-content-center items-center py-24 overflow-x-hidden">
      <div class="responsive-title-1 text-primary-darkBlue font-thin">Read more</div>
      <div class="px-1 grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <div v-for="(page, index) in blogStore.allArticles.slice(0, 3)" :key="index">
          <ArticleCard
              :page="page"
              @on-visit-article="articleStore.visitArticle(page.slug)"
          />
        </div>
      </div>
      <CustomButton
          type="gradient"
          :outlined="true"
          size="medium"
          :shadow="false"
          class="py-2 px-4"
          @click="router.push('/magazine')"
      >
        READ MORE ARTICLES
      </CustomButton>
    </div>
  </div>
</template>

